import { useMutation } from '@apollo/client';
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { CheckIcon } from '@heroicons/react/24/solid';
import Modal from '../common/modal.tsx';
import Button from '../common/button.tsx';
import HighlightProvier from '../../contexts/highlight-provider/index.tsx';
import { useHighlight } from '../../contexts/highlight-provider/useHighlight.ts';
import { useQuestionHighlightVisibility } from '../../contexts/highlight-provider/use-question-highlight-visibility.ts';
import { StoredHighlightDataT } from '../../contexts/highlight-provider/types.ts';
import QuestionContent, {
  ICompletedData,
} from '../questions/question-content/question-content.tsx';
import { ReviewTools } from '../questions/question-content/review-tools.tsx';
import {
  QuestionAnswerType,
  QuestionFragmentFragment,
} from '../../apollo/__generated__/graphql.ts';
import {
  QUESTION_LOG_REVIEW_STATUS,
  QuestionLogEntry,
  SAVE_QUESTION_LOG_REVIEW_ANNOTATIONS,
} from '../../apollo/question-log.ts';
import { capitalizeWords } from '../../utils/capitalize-words.ts';
import { QuestionLogStarButton } from './question-log-star-button.tsx';
import QuestionReportForm from '../questions/question-content/question-report-form.tsx';
import ReferenceModal from '../questions/question-content/reference-modal.tsx';
import { ReviewModeHeader } from '../questions/question-content/review-mode-header.tsx';
import { useReviewAnswerDefault } from '../questions/question-content/use-review-answer-default.ts';

const REVIEW_ANNOTATIONS_SAVE_DEBOUNCE_MS = 200;

interface IProps {
  entry?: QuestionLogEntry;
  isOpen: boolean;
  onClose: () => void;
  onNext: () => void;
  onPrev: () => void;
  nextDisabled: boolean;
  prevDisabled: boolean;
  onMarkReviewed: () => void;
  onMarkUnreviewed: () => void;
  onToggleStar: () => void;
  onToggleMarkedForReview: (mark: boolean) => void;
  isSaving?: boolean;
}

interface IReviewQuestionContentProps {
  entry: QuestionLogEntry;
  completedData?: ICompletedData;
  isAnswerVisible: boolean;
  onSetAnswerVisible: (value: boolean) => void;
  isExplanation: boolean;
  setIsExplanation: (value: boolean) => void;
  isCalc: boolean;
  setIsCalc: Dispatch<SetStateAction<boolean>>;
  isMark: boolean;
  setMark: (mark: boolean) => void;
  reviewStartsRevealed: boolean;
}

const QuestionLogReviewQuestionContent = ({
  entry,
  completedData,
  isAnswerVisible,
  onSetAnswerVisible,
  isExplanation,
  setIsExplanation,
  isCalc,
  setIsCalc,
  isMark,
  setMark,
  reviewStartsRevealed,
}: IReviewQuestionContentProps) => {
  const { highlightStickersToggle } = useHighlight();
  const { hasQuestionStickers, toggleQuestionNotesVisible } =
    useQuestionHighlightVisibility(entry.question.id);

  return (
    <QuestionContent
      questionId={entry.question.id}
      questionNumber={
        entry.lastQuestionOrder != null ? entry.lastQuestionOrder + 1 : undefined
      }
      prompt={entry.question.prompt}
      content={entry.question.content}
      isMark={isMark}
      setMark={setMark}
      explanation={entry.question.explanation}
      choicesData={
        entry.question.answerType === QuestionAnswerType.MultipleChoice
          ? (entry.question.choices as QuestionFragmentFragment['choices'])
          : undefined
      }
      isPreviewMode
      isMath={entry.question.section?.name === 'math'}
      completedData={completedData}
      isExplanation={isExplanation}
      setIsExplanation={setIsExplanation}
      isAnswerVisible={isAnswerVisible}
      setIsAnswerVisible={onSetAnswerVisible}
      stickyBottomActions
      allowReviewInteractions
      isShowHighLights
      highlightStickersToggle={highlightStickersToggle}
      hasQuestionStickers={hasQuestionStickers}
      onToggleNoteVisibility={toggleQuestionNotesVisible}
      isCalc={isCalc}
      setIsCalc={setIsCalc}
      reviewStartsRevealed={reviewStartsRevealed}
    />
  );
};

export const QuestionLogReviewModal = ({
  entry,
  isOpen,
  onClose,
  onNext,
  onPrev,
  nextDisabled,
  prevDisabled,
  onMarkReviewed,
  onMarkUnreviewed,
  onToggleStar,
  onToggleMarkedForReview,
  isSaving,
}: IProps) => {
  const navigationButtonClass =
    '!border-[#D7E3F2] !bg-[#F7FAFE] !text-dark-blue transition-colors hover:!border-[#C5D6EC] hover:!bg-[#EEF4FB] hover:!text-dark-blue';
  const [isExplanation, setIsExplanation] = useState(false);
  const [isAnswerVisible, setIsAnswerVisible] = useState(false);
  const [isCalc, setIsCalc] = useState(false);
  const [isReferenceOpen, setIsReferenceOpen] = useState(false);
  const [isReportOpen, setIsReportOpen] = useState(false);
  const [reviewAnnotations, setReviewAnnotations] = useState<
    StoredHighlightDataT[]
  >([]);
  const [answerVisibilityOverrides, setAnswerVisibilityOverrides] = useState<
    Record<string, boolean>
  >({});
  const [isAdvancedInfoOpen, setIsAdvancedInfoOpen] = useState(false);
  const [saveReviewAnnotations] = useMutation(SAVE_QUESTION_LOG_REVIEW_ANNOTATIONS);
  const { showAnswersByDefault, setShowAnswersByDefault } =
    useReviewAnswerDefault();
  const questionVisibilityKey = entry?.itemKey;
  const questionAnswerVisibilityOverride =
    questionVisibilityKey != null
      ? answerVisibilityOverrides[questionVisibilityKey]
      : undefined;
  const reviewStartsRevealed =
    questionAnswerVisibilityOverride ?? showAnswersByDefault;
  const reviewStartsRevealedRef = useRef(reviewStartsRevealed);

  reviewStartsRevealedRef.current = reviewStartsRevealed;

  const initialReviewAnnotations = useMemo(
    () =>
      ((entry?.reviewAnnotations as StoredHighlightDataT[] | null | undefined) ??
        []),
    [entry?.reviewAnnotations],
  );

  useEffect(() => {
    setIsExplanation(false);
    setIsAnswerVisible(reviewStartsRevealedRef.current);
    setIsCalc(false);
    setIsReferenceOpen(false);
    setIsReportOpen(false);
  }, [entry?.itemKey]);

  useEffect(() => {
    if (!isOpen) {
      setIsReferenceOpen(false);
      setIsReportOpen(false);
    }
  }, [isOpen]);

  useEffect(() => {
    setReviewAnnotations(initialReviewAnnotations);
  }, [entry?.itemKey, initialReviewAnnotations]);

  const onSetAnswerVisible = useCallback(
    (value: boolean) => {
      setIsAnswerVisible(value);

      if (!questionVisibilityKey) {
        return;
      }

      setAnswerVisibilityOverrides((current) => {
        if (value === showAnswersByDefault) {
          if (!(questionVisibilityKey in current)) {
            return current;
          }

          const nextOverrides = { ...current };
          delete nextOverrides[questionVisibilityKey];
          return nextOverrides;
        }

        return {
          ...current,
          [questionVisibilityKey]: value,
        };
      });
    },
    [questionVisibilityKey, showAnswersByDefault],
  );

  const onToggleShowAnswersByDefault = useCallback(
    (value: boolean) => {
      setShowAnswersByDefault(value);

      if (
        !questionVisibilityKey ||
        questionAnswerVisibilityOverride != null
      ) {
        return;
      }

      setIsAnswerVisible(value);
    },
    [
      questionAnswerVisibilityOverride,
      questionVisibilityKey,
      setShowAnswersByDefault,
    ],
  );

  const onRequestClose = useCallback(() => {
    if (isReferenceOpen) {
      setIsReferenceOpen(false);

      return;
    }

    if (isReportOpen) {
      setIsReportOpen(false);

      return;
    }

    onClose();
  }, [isReferenceOpen, isReportOpen, onClose]);

  useEffect(() => {
    if (!entry?.question.id) {
      return;
    }

    if (
      JSON.stringify(reviewAnnotations) === JSON.stringify(initialReviewAnnotations)
    ) {
      return;
    }

    const timeoutId = window.setTimeout(() => {
      void saveReviewAnnotations({
        variables: {
          input: {
            questionId: entry.question.id,
            reviewAnnotations,
          },
        },
      });
    }, REVIEW_ANNOTATIONS_SAVE_DEBOUNCE_MS);

    return () => window.clearTimeout(timeoutId);
  }, [
    entry?.question.id,
    initialReviewAnnotations,
    reviewAnnotations,
    saveReviewAnnotations,
  ]);

  const correctChoice = useMemo(
    () => entry?.question.choices?.find((choice) => choice.correct),
    [entry?.question.choices],
  );

  const completedData = useMemo<ICompletedData | undefined>(() => {
    if (!entry) {
      return undefined;
    }

    return {
      userAnswer:
        entry.question.answerType === QuestionAnswerType.MultipleChoice
          ? entry.lastChoiceId
          : entry.lastText,
      correctAnswer:
        entry.question.answerType === QuestionAnswerType.MultipleChoice
          ? correctChoice?.id
          : correctChoice?.text,
      isCorrect: false,
    };
  }, [correctChoice?.id, correctChoice?.text, entry]);

  const questionIdentifier = entry?.question.originalId ?? entry?.question.id;
  const isMath = entry?.question.section?.name === 'math';

  const footerMeta = useMemo(
    () =>
      [
        entry?.question.section?.name
          ? `Section: ${capitalizeWords(entry.question.section.name)}`
          : null,
        entry?.question.domain?.name
          ? `Domain: ${capitalizeWords(entry.question.domain.name)}`
          : null,
        entry?.question.skill?.name
          ? `Skill: ${capitalizeWords(entry.question.skill.name)}`
          : null,
        entry?.question.type1?.name
          ? `Qtype: ${capitalizeWords(entry.question.type1.name)}`
          : null,
        entry?.wrongCount != null ? `Attempts: ${entry.wrongCount}` : null,
      ].filter((item): item is string => Boolean(item)),
    [entry],
  );

  const isReviewed =
    entry?.reviewStatus === QUESTION_LOG_REVIEW_STATUS.Reviewed;

  return entry ? (
    <HighlightProvier
      key={`question-log-review-${entry.itemKey}`}
      storageKey={`review-annotations:${entry.question.id}`}
      initialData={
        initialReviewAnnotations.length ? initialReviewAnnotations : null
      }
      onChange={setReviewAnnotations}
    >
      <>
        <Modal
          isOpen={isOpen}
          onClose={onRequestClose}
          title={
            questionIdentifier != null
              ? `Question Review #${questionIdentifier}`
              : 'Question Review'
          }
          titleContent={
            <div className="ml-2 text-[13px] font-medium text-black">
              {entry.lastSourceName ? `Source: ${entry.lastSourceName}` : ''}
            </div>
          }
          centerContent={
            <ReviewModeHeader
              showAnswersByDefault={showAnswersByDefault}
              onToggleShowAnswersByDefault={onToggleShowAnswersByDefault}
            />
          }
          headerActions={
            <ReviewTools
              isMath={!!isMath}
              isCalc={isCalc}
              onToggleCalc={() => setIsCalc((prevState) => !prevState)}
              showReport
              onOpenReport={() => setIsReportOpen(true)}
              showReference
              isReferenceOpen={isReferenceOpen}
              onOpenReference={() => setIsReferenceOpen(true)}
            />
          }
          fullScreen
          headerClassName="!my-0 !-mx-4 border-b border-black/10 bg-[#e7edf7] !px-4 !py-2"
          titleClassName="text-[13px] font-semibold"
          titleRowClassName="w-full gap-3"
          panelClassName="flex h-full w-full flex-col !overflow-hidden !px-4 !pb-4 !pt-0"
          bodyClassName="min-h-0 flex flex-1 flex-col"
        >
          <div className="flex min-h-0 flex-1 flex-col">
            <div className="min-h-0 flex-1">
              <QuestionLogReviewQuestionContent
                entry={entry}
                completedData={completedData}
                isExplanation={isExplanation}
                setIsExplanation={setIsExplanation}
                isAnswerVisible={isAnswerVisible}
                onSetAnswerVisible={onSetAnswerVisible}
                isCalc={isCalc}
                setIsCalc={setIsCalc}
                isMark={entry.markedForReview}
                setMark={onToggleMarkedForReview}
                reviewStartsRevealed={reviewStartsRevealed}
              />
            </div>

            <div className="mt-3 shrink-0 rounded-2xl border border-table-border bg-white px-4 py-3 shadow-[0_-8px_24px_rgba(15,23,42,0.06)]">
              <div className="flex flex-col gap-2 lg:flex-row lg:items-center lg:justify-between">
                <div className="flex items-center gap-2">
                  <QuestionLogStarButton
                    starred={entry.starred}
                    onClick={onToggleStar}
                    disabled={isSaving}
                  />
                  {!!footerMeta.length && (
                    <Button
                      outlined
                      className={navigationButtonClass}
                      onClick={() =>
                        setIsAdvancedInfoOpen((currentState) => !currentState)
                      }
                    >
                      {isAdvancedInfoOpen
                        ? 'Hide Advanced Info'
                        : 'Show Advanced Info'}
                    </Button>
                  )}
                </div>

                <div className="flex flex-wrap items-center gap-2 lg:justify-end">
                  {(isSaving || isReviewed) && (
                    <div className="text-sm text-gray">
                      {isSaving
                        ? 'Saving your update...'
                        : 'This question has been completed.'}
                    </div>
                  )}
                  {isReviewed ? (
                    <>
                      <div className="inline-flex items-center gap-1 rounded-full bg-[#E5F6EA] px-3 py-2 text-sm font-semibold text-[#1F7A3D]">
                        <span>Completed</span>
                        <CheckIcon className="h-4 w-4" />
                      </div>
                      <button
                        type="button"
                        className="px-2 py-2 text-sm font-medium text-gray underline-offset-2 transition-colors hover:text-dark-blue hover:underline disabled:cursor-not-allowed disabled:opacity-60"
                        onClick={onMarkUnreviewed}
                        disabled={isSaving}
                      >
                        Undo
                      </button>
                    </>
                  ) : (
                    <>
                      {!isSaving && (
                        <div className="text-sm text-gray">
                          This question is still unreviewed.
                        </div>
                      )}
                      <button
                        type="button"
                        className="rounded-full bg-[#E8F0FA] px-4 py-2 text-sm font-semibold text-blue transition-colors hover:bg-[#D9E7F6] disabled:cursor-not-allowed disabled:opacity-60"
                        onClick={onMarkReviewed}
                        disabled={isSaving}
                      >
                        Mark as Reviewed
                      </button>
                    </>
                  )}
                  {!prevDisabled && (
                    <Button
                      outlined
                      className={navigationButtonClass}
                      onClick={onPrev}
                    >
                      Previous
                    </Button>
                  )}
                  {!nextDisabled && (
                    <Button
                      outlined
                      className={navigationButtonClass}
                      onClick={onNext}
                    >
                      Next
                    </Button>
                  )}
                </div>
              </div>

              {isAdvancedInfoOpen && !!footerMeta.length && (
                <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 border-t border-black/10 pt-3 text-[13px] font-medium text-black">
                  {footerMeta.map((item) => (
                    <span key={item}>{item}</span>
                  ))}
                </div>
              )}
            </div>
          </div>
        </Modal>
        <ReferenceModal
          isOpen={isReferenceOpen}
          onClose={() => setIsReferenceOpen(false)}
        />
        <Modal
          title={'Question Report'}
          onClose={() => setIsReportOpen(false)}
          isOpen={isReportOpen}
        >
          <QuestionReportForm
            questionId={entry.question.id}
            close={() => setIsReportOpen(false)}
          />
        </Modal>
      </>
    </HighlightProvier>
  ) : null;
};
